import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, User, PenSquare } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button.jsx';
import { useAuth } from '../context/AuthContext';
import EnrollBlock from '../components/EnrollBlock';
import InnerCircleBottomBar from '../components/InnerCircleBottomBar';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'All', 'Tantra', 'Yoga', 'Pranayama', 'Meditation', 'Philosophy',
  'Lifestyle, Diet & Health', 'Ayurveda', 'Lineage', 'Practice', 'Consciousness',
];

const STATUS_BADGE = {
  draft: 'bg-slate-500/15 text-slate-300 border-slate-400/30',
  pending_review: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
};
const STATUS_LABEL = { draft: 'Draft', pending_review: 'Pending', rejected: 'Rejected' };

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Blog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [role, setRole] = useState('reader');
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.get(`${API}/blog`, { headers });
        setPosts(r.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/blog/my-role`, { headers: authHeader() })
      .then((r) => setRole(r.data.role))
      .catch(() => setRole('reader'));
  }, [user]);

  useEffect(() => {
    if (!user || role === 'reader') { setMyPosts([]); return; }
    axios.get(`${API}/blog/my-posts`, { headers: authHeader() })
      .then((r) => setMyPosts(r.data || []))
      .catch(() => setMyPosts([]));
  }, [user, role]);

  const canWrite = role === 'writer' || role === 'admin';

  const visiblePosts = useMemo(() => {
    // Merge: published (everyone) + own drafts/pending/rejected for writer/admin
    const byId = new Map();
    for (const p of posts) byId.set(p.id, p);
    if (canWrite) {
      for (const p of myPosts) {
        if (p.status !== 'published') byId.set(p.id, p);
      }
    }
    let all = Array.from(byId.values());
    if (category !== 'All') all = all.filter((p) => p.category === category);
    all.sort((a, b) => (b.published_at || b.updated_at || '').localeCompare(a.published_at || a.updated_at || ''));
    return all;
  }, [posts, myPosts, canWrite, category]);

  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 relative">
      <SEO
        title="Wisdom & Insights — MyShaktiverse"
        description="Writings on Tantra, Yoga, Pranayama, and the Siddha path."
        path="/blog"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-6xl font-heading golden-text tracking-wider mb-4">
            Wisdom & Insights
          </h1>
          <p className="text-base md:text-lg text-primary/70 max-w-2xl mx-auto">
            Explore writings on Tantra, Yoga, and spiritual practice
          </p>
        </motion.div>

        {canWrite && (
          <div className="flex justify-end mb-6">
            <Button
              onClick={() => navigate('/blog/new')}
              className="ornate-button"
              data-testid="blog-write-post-btn"
            >
              <PenSquare size={16} className="mr-2" /> Write a Post
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-1.5 rounded-full border text-xs font-heading uppercase tracking-[0.2em] transition-colors ${
                category === c
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-primary/25 text-primary/70 hover:border-primary/60 hover:text-primary'
              }`}
              data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-primary/60 py-16">Loading…</div>
        ) : visiblePosts.length === 0 ? (
          <div className="text-center text-primary/60 py-16">No posts in this category yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {visiblePosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                onClick={() => navigate(post.status === 'published' ? `/blog/${post.slug}` : `/blog/edit/${post.slug}`)}
                className="card-mystical rounded-2xl overflow-hidden cursor-pointer group"
                data-testid={`blog-post-${post.id}`}
              >
                <div className="aspect-[16/9] overflow-hidden bg-primary/5">
                  {post.cover_image_url ? (
                    <img
                      src={post.cover_image_url}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
                  )}
                </div>
                <div className="p-6 relative">
                  <div className="flex items-center gap-2 text-xs text-primary/70 mb-3 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium border border-primary/20">
                      {post.category}
                    </span>
                    {post.status !== 'published' && STATUS_LABEL[post.status] && (
                      <span className={`px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.2em] ${STATUS_BADGE[post.status]}`}>
                        {STATUS_LABEL[post.status]}
                      </span>
                    )}
                    <div className="flex items-center gap-1 ml-auto">
                      <Clock size={14} />
                      <span>{post.read_time || 1} min read</span>
                    </div>
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-primary line-clamp-2 mb-2">
                    {post.title}
                  </h2>
                  {post.subtitle && (
                    <p className="text-sm text-primary/70 line-clamp-2 mb-4">{post.subtitle}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-primary/60">
                    <User size={14} />
                    <span>{post.author_name}</span>
                    {post.inner_circle_only && (
                      <span
                        className="ml-auto inline-flex items-center text-[10px] font-heading uppercase tracking-[0.25em] rounded-full border border-primary/55 text-primary px-2 py-0.5"
                        data-testid={`blog-card-ic-seal-${post.id}`}
                      >
                        Inner Circle only
                      </span>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <div className="mt-16">
          <EnrollBlock onSelfPacedClick={() => navigate('/courses/1')} />
        </div>
      </div>
      <InnerCircleBottomBar />
    </div>
  );
};

export default Blog;
